import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";
import "./DashboardPlacagesDensite.css";

const ALL_JOURS = "__all__";
const ALL_LARGEURS = "__all__";
const ALL_OPERATEURS = "__all__";
const HORS_NOMINAL_PAGE_SIZE = 15;

const TAB_CONTROLES = "controles";
const TAB_BANC = "banc";

const TAB_ITEMS = [
  {
    id: TAB_CONTROLES,
    label: "Contrôles du jour",
    hint: "Placages contrôlés à la date (ou aux dates) sélectionnées — empilés ou non.",
  },
  {
    id: TAB_BANC,
    label: "Banc non empilé",
    hint: "Uniquement les plis encore au banc de test dont le numéro n’apparaît dans aucun empilement.",
  },
];

function trierLargeurKeys(keys) {
  const nominales = keys.filter((k) => k !== "hors_nominal").map(Number).sort((a, b) => a - b).map(String);
  return keys.includes("hors_nominal") ? [...nominales, "hors_nominal"] : nominales;
}

function cellVide() {
  return { basse: 0, haute: 0, total: 0, largeur_affichee: null };
}

function dedupeMesuresByLot(rows) {
  const seen = new Set();
  const out = [];
  for (const m of rows) {
    const key = String(m.lot || "")
      .replace(/\s+/g, "")
      .replace(/[-_]/g, "")
      .toUpperCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(m);
  }
  return out;
}

/** Filtre selon l’onglet actif. */
function filterMesuresForTab(mesures, dayKeys, jour, tab) {
  if (!mesures?.length) return [];
  if (tab === TAB_CONTROLES) {
    if (!dayKeys?.length) return [];
    return mesures.filter((m) => dayKeys.includes(m.jour));
  }
  let rows = mesures.filter((m) => m.pendingEmpilement);
  if (jour !== ALL_JOURS && dayKeys?.length) {
    rows = rows.filter((m) => dayKeys.includes(m.jour));
  }
  return rows;
}

function aggregateMesures(mesures, dayKeys, jour, largeurKey, tab) {
  if (!mesures?.length) return cellVide();
  if (tab === TAB_CONTROLES && !dayKeys?.length) return cellVide();
  let rows = dedupeMesuresByLot(filterMesuresForTab(mesures, dayKeys, jour, tab));
  if (largeurKey !== ALL_LARGEURS) {
    rows = rows.filter((m) => m.largeurKey === largeurKey);
  }
  if (!rows.length) return cellVide();

  let basse = 0;
  let haute = 0;
  let wSum = 0;
  for (const m of rows) {
    if (m.categorie === "basse") basse += 1;
    else haute += 1;
    wSum += m.largeur_mesuree;
  }
  const total = rows.length;
  return {
    basse,
    haute,
    total,
    largeur_affichee: Math.round(wSum / total),
  };
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  return (
    <div
      style={{
        background: "#0e0e10",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 8,
        padding: "10px 14px",
        fontSize: 12,
      }}
    >
      <div style={{ color: "#e6ff3e", fontWeight: 700, marginBottom: 4 }}>{p?.label}</div>
      <div style={{ color: "#9a9a92" }}>
        {p?.count} pli(s) · {p?.pct} %
      </div>
    </div>
  );
}

export default function DashboardPlacagesDensite() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [jour, setJour] = useState("");
  const [largeur, setLargeur] = useState("");
  const [operateur, setOperateur] = useState(ALL_OPERATEURS);
  const [activeTab, setActiveTab] = useState(TAB_CONTROLES);
  const [horsNominalPage, setHorsNominalPage] = useState(1);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/dashboard/qualite/placages-densite");
      const json = await res.json();
      if (!res.ok || json.status !== "success") {
        throw new Error(json.message || `Erreur HTTP ${res.status}`);
      }
      setData(json);

      const today = json.jours?.[0] || "";
      setJour((prev) =>
        prev === ALL_JOURS || json.jours?.includes(prev) ? prev : today
      );
      setLargeur((prev) => {
        if (prev === ALL_LARGEURS) return prev;
        const dayKeys = trierLargeurKeys(Object.keys(json.table?.[today] || {}));
        return dayKeys.includes(prev) ? prev : dayKeys[0] || ALL_LARGEURS;
      });
      setOperateur((prev) =>
        prev === ALL_OPERATEURS || json.operateurs?.includes(prev) ? prev : ALL_OPERATEURS
      );
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const mesuresByOperateur = useMemo(() => {
    const all = data?.mesures || [];
    if (operateur === ALL_OPERATEURS) return all;
    return all.filter((m) => m.operateur === operateur);
  }, [data, operateur]);

  const joursDisponibles = useMemo(() => {
    const set = new Set(mesuresByOperateur.map((m) => m.jour));
    return (data?.jours || []).filter((j) => set.has(j));
  }, [data, mesuresByOperateur]);

  const dayKeys = useMemo(() => {
    if (!jour) return [];
    if (jour === ALL_JOURS) return joursDisponibles;
    return [jour];
  }, [jour, joursDisponibles]);

  const operateursDisponibles = useMemo(() => {
    const pool = filterMesuresForTab(
      data?.mesures || [],
      dayKeys,
      jour,
      activeTab
    );
    const set = new Set(pool.map((m) => m.operateur));
    return [...set].sort((a, b) => a.localeCompare(b, "fr", { sensitivity: "base" }));
  }, [data, jour, dayKeys, activeTab]);

  const mesuresScope = mesuresByOperateur;

  useEffect(() => {
    if (operateur === ALL_OPERATEURS) return;
    if (operateursDisponibles.includes(operateur)) return;
    setOperateur(ALL_OPERATEURS);
  }, [jour, operateursDisponibles, operateur]);

  useEffect(() => {
    if (!data || jour === ALL_JOURS) return;
    if (joursDisponibles.includes(jour)) return;
    setJour(joursDisponibles[0] || ALL_JOURS);
  }, [data, jour, joursDisponibles]);

  const mesuresForDayScope = useMemo(
    () =>
      dedupeMesuresByLot(
        filterMesuresForTab(mesuresScope, dayKeys, jour, activeTab)
      ),
    [mesuresScope, dayKeys, jour, activeTab]
  );

  const tabHint = TAB_ITEMS.find((t) => t.id === activeTab)?.hint || "";

  const largeurOptions = useMemo(() => {
    if (!dayKeys.length) return [];
    const keys = new Set(mesuresForDayScope.map((m) => m.largeurKey));
    return trierLargeurKeys([...keys]);
  }, [mesuresForDayScope, dayKeys]);

  useEffect(() => {
    if (jour === ALL_JOURS && largeur === ALL_LARGEURS) return;
    if (!largeurOptions.length) {
      if (largeur !== ALL_LARGEURS) setLargeur(ALL_LARGEURS);
      return;
    }
    if (largeur === ALL_LARGEURS || largeurOptions.includes(largeur)) return;
    setLargeur(largeurOptions[0]);
  }, [largeurOptions, largeur, jour]);

  const cell = useMemo(() => {
    if (!data || !jour || !largeur) return cellVide();
    return aggregateMesures(mesuresScope, dayKeys, jour, largeur, activeTab);
  }, [data, jour, largeur, dayKeys, mesuresScope, activeTab]);

  const chartData = useMemo(() => {
    const total = cell.total || 0;
    const pctBasse = total ? Math.round((cell.basse / total) * 100) : 0;
    const pctHaute = total ? Math.round((cell.haute / total) * 100) : 0;
    return [
      {
        key: "basse",
        label: "Densité basse",
        count: cell.basse,
        pct: pctBasse,
        barLabel: `${cell.basse} (${pctBasse.toFixed(0)} %)`,
        fill: data?.cfg?.couleurBasse || "#4C78A8",
      },
      {
        key: "haute",
        label: "Densité haute",
        count: cell.haute,
        pct: pctHaute,
        barLabel: `${cell.haute} (${pctHaute.toFixed(0)} %)`,
        fill: data?.cfg?.couleurHaute || "#F58518",
      },
    ];
  }, [cell, data]);

  const horsNominalJour = useMemo(() => {
    if (!jour) return [];
    let rows = mesuresForDayScope.filter((m) => m.largeurKey === "hors_nominal");
    return rows;
  }, [mesuresForDayScope, jour]);

  const showHorsNominalSection =
    horsNominalJour.length > 0 &&
    (largeur === ALL_LARGEURS || largeur === "hors_nominal");

  useEffect(() => {
    setHorsNominalPage(1);
  }, [jour, largeur, operateur, activeTab]);

  const horsNominalTotalPages = Math.max(
    1,
    Math.ceil(horsNominalJour.length / HORS_NOMINAL_PAGE_SIZE)
  );

  useEffect(() => {
    if (horsNominalPage > horsNominalTotalPages) {
      setHorsNominalPage(horsNominalTotalPages);
    }
  }, [horsNominalPage, horsNominalTotalPages]);

  const horsNominalStart = (horsNominalPage - 1) * HORS_NOMINAL_PAGE_SIZE;
  const horsNominalPageRows = horsNominalJour.slice(
    horsNominalStart,
    horsNominalStart + HORS_NOMINAL_PAGE_SIZE
  );

  return (
    <div className="plac-page">
      <MenuBar active="dashboard" />
      <div className="plac-wrap">
        <header className="plac-hero">
          <div>
            <p className="plac-kicker">Dashboard · Qualité</p>
            <h1 className="plac-title">Suivi placages contrôlés</h1>
          </div>
          <button type="button" className="plac-back" onClick={() => navigate(-1)}>
            Retour
          </button>
        </header>

        <div className="plac-actions">
          <button type="button" className="plac-btn" onClick={loadData}>
            Actualiser
          </button>
        </div>

        {loading && <div className="plac-loading">Chargement des mesures banc…</div>}
        {error && !loading && <div className="plac-error">{error}</div>}

        {!loading && !error && data && (
          <div className="plac-card">
            <div className="plac-tabs" role="tablist" aria-label="Mode de comptage">
              {TAB_ITEMS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`plac-tab ${activeTab === tab.id ? "plac-tab-active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {tabHint ? <p className="plac-tab-hint">{tabHint}</p> : null}
            <div className="plac-filtres">
              <div className="plac-filtre">
                <label htmlFor="filtre-jour">Jour</label>
                <select
                  id="filtre-jour"
                  className="plac-sel"
                  value={jour}
                  onChange={(e) => setJour(e.target.value)}
                >
                  <option value={ALL_JOURS}>Tous</option>
                  {joursDisponibles.map((j) => (
                    <option key={j} value={j}>
                      {data.jourLabels?.[j] || j}
                    </option>
                  ))}
                </select>
              </div>
              <div className="plac-filtre">
                <label htmlFor="filtre-operateur">Opérateur</label>
                <select
                  id="filtre-operateur"
                  className="plac-sel"
                  value={operateur}
                  onChange={(e) => setOperateur(e.target.value)}
                >
                  <option value={ALL_OPERATEURS}>Tous</option>
                  {operateursDisponibles.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </div>
              <div className="plac-filtre">
                <label htmlFor="filtre-largeur">Largeur</label>
                <select
                  id="filtre-largeur"
                  className="plac-sel"
                  value={largeur}
                  onChange={(e) => setLargeur(e.target.value)}
                  disabled={!jour}
                >
                  <option value={ALL_LARGEURS}>Tous</option>
                  {largeurOptions.length === 0 ? (
                    <option value="" disabled>
                      Aucune largeur pour cette sélection
                    </option>
                  ) : (
                    largeurOptions.map((k) => (
                      <option key={k} value={k}>
                        {data.largeurLabels?.[k] || k}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            {cell.total > 0 ? (
              <>
                <div className="plac-chart-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 44, right: 16, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: "#9a9a92", fontSize: 13 }}
                        axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fill: "#9a9a92", fontSize: 11 }}
                        axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
                        label={{
                          value: "Nombre de plis",
                          angle: -90,
                          position: "insideLeft",
                          fill: "#6e6e66",
                          fontSize: 11,
                        }}
                      />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                      <Bar dataKey="count" radius={[8, 8, 0, 0]} maxBarSize={120}>
                        {chartData.map((entry) => (
                          <Cell key={entry.key} fill={entry.fill} />
                        ))}
                        <LabelList
                          dataKey="barLabel"
                          position="top"
                          offset={10}
                          fill="#f4f4f1"
                          fontSize={14}
                          fontWeight={700}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="plac-legend">
                  <span>
                    <span className="plac-dot" style={{ background: data.cfg?.couleurBasse }} />
                    Densité basse (&lt; {data.cfg?.seuilDensite})
                  </span>
                  <span>
                    <span className="plac-dot" style={{ background: data.cfg?.couleurHaute }} />
                    Densité haute (≥ {data.cfg?.seuilDensite})
                  </span>
                </div>
              </>
            ) : (
              <div className="plac-empty">
                {activeTab === TAB_BANC
                  ? "Aucun pli au banc non empilé pour cette sélection."
                  : "Aucun pli contrôlé pour cette sélection."}
              </div>
            )}
          </div>
        )}

        {!loading && !error && showHorsNominalSection && (
          <section className="plac-hors">
            <h2 className="plac-hors-hd">
              Plis hors nominal
              {jour === ALL_JOURS ? " (tous les jours)" : " ce jour"} (
              {horsNominalJour.length})
            </h2>
            <div className="plac-table-wrap">
              <table className="plac-table">
                <thead>
                  <tr>
                    {jour === ALL_JOURS && <th>Jour</th>}
                    {operateur === ALL_OPERATEURS && <th>Opérateur</th>}
                    <th>Lot</th>
                    <th>Largeur mes.</th>
                    <th>Dist. nominale</th>
                    <th>Densité</th>
                    <th>Catégorie</th>
                  </tr>
                </thead>
                <tbody>
                  {horsNominalPageRows.map((r) => (
                    <tr key={`${r.jour}-${r.lot}-${r.largeur_mesuree}`}>
                      {jour === ALL_JOURS && (
                        <td>{data.jourLabels?.[r.jour] || r.jour}</td>
                      )}
                      {operateur === ALL_OPERATEURS && <td>{r.operateur}</td>}
                      <td>{r.lot}</td>
                      <td>{r.largeur_mesuree} mm</td>
                      <td>{r.distance_nominale} mm</td>
                      <td>{r.densite}</td>
                      <td>{r.categorie}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {horsNominalJour.length > HORS_NOMINAL_PAGE_SIZE && (
              <div className="plac-pagination">
                <button
                  type="button"
                  className="plac-page-btn"
                  disabled={horsNominalPage === 1}
                  onClick={() => setHorsNominalPage((p) => Math.max(1, p - 1))}
                >
                  ← Préc.
                </button>
                <span className="plac-page-info">
                  Page {horsNominalPage} / {horsNominalTotalPages}
                  <em>
                    {horsNominalStart + 1}–
                    {Math.min(horsNominalStart + HORS_NOMINAL_PAGE_SIZE, horsNominalJour.length)}{" "}
                    sur {horsNominalJour.length}
                  </em>
                </span>
                <button
                  type="button"
                  className="plac-page-btn"
                  disabled={horsNominalPage === horsNominalTotalPages}
                  onClick={() =>
                    setHorsNominalPage((p) => Math.min(horsNominalTotalPages, p + 1))
                  }
                >
                  Suiv. →
                </button>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
